import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { EditorState } from "@tiptap/pm/state";

interface SectionVisibilityState {
  sectionToggles: Record<string, boolean>;
  collapsedSections: Record<string, boolean>;
  decorations: DecorationSet;
}

export const sectionVisibilityKey = new PluginKey<SectionVisibilityState>("sectionVisibility");

function buildDecorations(
  state: EditorState,
  sectionToggles: Record<string, boolean>,
  collapsedSections: Record<string, boolean>,
): DecorationSet {
  const decos: Decoration[] = [];
  let sectionVisible = true;
  let sectionCollapsed = false;

  let offset = 0;
  for (let i = 0; i < state.doc.childCount; i++) {
    const node = state.doc.child(i);
    if (node.type.name === "section") {
      const id = node.attrs.sectionId as string | null;
      sectionVisible = id ? (sectionToggles[id] ?? true) : true;
      sectionCollapsed = id ? (collapsedSections[id] ?? false) : false;
      // Headings are always visible so the user can re-enable a toggled-off section
    } else if (!sectionVisible || sectionCollapsed) {
      // Hide everything else when the section is toggled off OR collapsed
      decos.push(Decoration.node(offset, offset + node.nodeSize, { style: "display:none" }));
    }
    offset += node.nodeSize;
  }

  return DecorationSet.create(state.doc, decos);
}

function applyVisibilityState(
  tr: Parameters<NonNullable<ConstructorParameters<typeof Plugin>[0]["state"]>["apply"]>[0],
  prev: SectionVisibilityState,
  _oldState: EditorState,
  newState: EditorState,
): SectionVisibilityState {
  const meta = tr.getMeta(sectionVisibilityKey) as
    | { sectionToggles: Record<string, boolean>; collapsedSections: Record<string, boolean> }
    | undefined;

  if (meta) {
    return {
      collapsedSections: meta.collapsedSections,
      decorations: buildDecorations(newState, meta.sectionToggles, meta.collapsedSections),
      sectionToggles: meta.sectionToggles,
    };
  }

  if (tr.docChanged) {
    return {
      ...prev,
      decorations: buildDecorations(newState, prev.sectionToggles, prev.collapsedSections),
    };
  }

  return prev;
}

export const SectionVisibilityExtension = Extension.create({
  addProseMirrorPlugins() {
    return [
      new Plugin<SectionVisibilityState>({
        key: sectionVisibilityKey,
        props: {
          decorations(state) {
            return sectionVisibilityKey.getState(state)?.decorations;
          },
        },
        state: {
          apply: applyVisibilityState,
          init(_, editorState) {
            return {
              collapsedSections: {},
              decorations: buildDecorations(editorState, {}, {}),
              sectionToggles: {},
            };
          },
        },
      }),
    ];
  },

  name: "sectionVisibility",
});
